import React, { useEffect, useState } from "react";
import { Document, Page, Font, Text, View, Image, StyleSheet } from "@react-pdf/renderer";
import { formatMoney, getCurrencyFromCurrencyCode, hasHTMLTag, sanitizeHtml } from "helpers";
import moment from "moment";
// @ts-ignore
import cairo from "./../../../../assets/fonts/cairo/cairo-v30-arabic_latin_latin-ext-700.ttf";

type PurchaseOrdersPDFProps = {
    vendorName: string;
    vendorPhone: string;
    vendorAddress: string;
    vendorEmail: string;
    ldbName: string;
    ldbPhone: string;
    ldbEmail: string;
    ldbAddress: string;
    imageDetails: string;
    title?: string;
    totalQuantity: number;
    totalAmount: number;
    taxAmount: number;
    filesAttached?: { [key: string]: any };
    containers: { [key: string]: any }[];
    customerDetails?: { [key: string]: any };
    approvers?: { [key: string]: any };
    taId?: string;
    poPrefix?: string;
};

async function imageUrlToBase64(url) {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error("Error converting URL to Base64:", error);
        return null;
    }
}

async function svgBase64ToPngBase64(
    svgBase64: string,
    width?: number,
    height?: number
): Promise<string> {
    return new Promise((resolve, reject) => {
        // Correct fix: Use window.Image to refer to the global HTMLImageElement constructor
        const img = new window.Image();

        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = width || img.naturalWidth;
            canvas.height = height || img.naturalHeight;
            const ctx = canvas.getContext("2d");

            if (!ctx) {
                reject(new Error("Could not get 2D rendering context for canvas."));
                return;
            }

            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/png"));
        };

        img.onerror = (errorEvent) => {
            console.error("Error loading SVG for canvas conversion:", errorEvent);
            reject(new Error("Failed to load SVG for canvas conversion."));
        };

        img.src = svgBase64;
    });
}

const PurchaseOrdersPDF = ({
    imageDetails,
    title,
    containers,
    customerDetails,
    taId,
    poPrefix,
    ldbName,
    ldbEmail,
    ldbAddress,
    vendorName,
    vendorEmail,
    vendorAddress,
    totalQuantity,
    totalAmount,
    taxAmount,
    filesAttached,
    ldbPhone,
    vendorPhone,
}: PurchaseOrdersPDFProps) => {
    Font.register({
        family: "inter",
        fonts: [
            {
                src: "../../../../assets/fonts/InterDisplay-SemiBold.otf",
                fontWeight: "medium",
            },
        ],
    });

    Font.register({
        family: "Cairo",
        fonts: [
            {
                src: cairo,
                fontWeight: 700,
            },
        ],
    });

    const signFix = StyleSheet.create({
        page: {
            fontFamily: "Roboto",
            fontWeight: 400,
        },
    });
    const styles = StyleSheet.create({
        page: {
            flexDirection: "column",
            backgroundColor: "#FFF",
            padding: 20,
            fontFamily: "inter",
            fontWeight: "medium",
        },
        bgWhite: {
            backgroundColor: "#FFF",
        },
        pageNumber: {
            position: "absolute",
            fontSize: 12,
            bottom: 5,
            left: 0,
            right: 0,
            textAlign: "center",
        },
        rounded: {
            borderRadius: 4,
        },
        textColor: {
            color: "#042821",
        },

        wFull: {
            width: "100%",
        },
        borderDashed: {
            borderStyle: "dashed",
            borderWidth: 1,
            borderColor: "#ccc",
        },
        flex: {
            display: "flex",
            flexDirection: "row",
        },
        justifyBetween: {
            justifyContent: "space-between",
        },
        itemsCenter: {
            alignItems: "center",
        },
        textGray300: {
            color: "#ccc",
        },
        textXs: {
            fontSize: 10,
        },

        textSm: {
            fontSize: "12px",
        },
        textGray500: {
            color: "#888",
        },
        textGm50: {
            color: "#042821",
        },
        textCenter: {
            textAlign: "center",
        },
        borderBGm: {
            borderBottomWidth: 1,
            borderBottomColor: "#142837",
        },
        borderBSlate: {
            borderBottomWidth: 1,
            borderBottomColor: "#F1F5F9",
        },
        spaceY2: {
            marginVertical: 8,
        },
        logo: {
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
        },
        text16: {
            fontSize: "16px",
        },
        section: {
            marginBottom: 10,
        },
        label: {
            fontSize: 12,
            fontWeight: "bold",
        },
        fontMedium: {
            fontWeight: "medium",
        },
        content: {
            fontSize: 12,
        },
        w1_3: {
            width: "33.33%",
        },
        w1_2: {
            width: "50%",
        },
        w2_5: {
            width: "40%",
        },
        w2_3: {
            width: "66.67%",
        },
        mb3: {
            marginBottom: 3,
        },
        mb1: {
            marginBottom: 1,
        },
        mb4: {
            marginBottom: 4,
        },
        mb6: {
            marginBottom: 6,
        },
        mb10: {
            marginBottom: 10,
        },
        mt4: {
            marginTop: 4,
        },
        pb3: {
            paddingBottom: 3,
        },
        pb2: {
            paddingBottom: 2,
        },
        pb4: {
            paddingBottom: 4,
        },
        pt2: {
            paddingTop: 2,
        },
        pt4: {
            paddingTop: 4,
        },
        pb6: {
            paddingBottom: 6,
        },
        pb8: {
            paddingBottom: 8,
        },
        my4: {
            marginBottom: 4,
            marginTop: 4,
        },
        py8: {
            paddingBottom: 8,
            paddingTop: 8,
        },
        py20: {
            paddingBottom: 20,
            paddingTop: 20,
        },
        pb20: {
            paddingBottom: 20,
        },
        pb5: {
            paddingBottom: 5,
        },
        mt20: {
            marginTop: 20,
        },
        my10: {
            marginBottom: 10,
            marginTop: 10,
        },
        mt1: {
            marginTop: 1,
        },
        mt10: {
            marginTop: 10,
        },
        pdfImage: {
            width: 20,
            height: 20,
            marginLeft: 3,
        },
    });

    const headerStyles = StyleSheet.create({
        container: {
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: 30,
            marginTop: 10,
        },
        imageContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 80,
            height: 80,
        },

        label: {
            fontSize: 16,
            color: "#ccc",
        },
    });
    const tableStyles = StyleSheet.create({
        section: {
            marginTop: 20,
            backgroundColor: "#FFB902",
            color: "#042821",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
        },
        headingSection: {
            marginBottom: 2,
            color: "#334155",
            fontWeight: "medium",
        },
        heading: {
            fontSize: 16,
            marginBottom: "16px",
            color: "#1E293B",
            borderBottom: "1px solid #042821",
        },
        container: {
            width: "100%",
            backgroundColor: "white",
        },
        tableBody: {
            borderLeft: "1 solid #042821",
            borderRight: "1 solid #042821",
            borderBottom: "1 solid #042821",
        },
        header: {
            flexDirection: "row",
            justifyContent: "space-between",
            borderLeft: "1 solid #042821",
            borderTop: "1 solid #042821",
            borderRight: "1 solid #042821",
            textAlign: "left",
            fontSize: 10,
            backgroundColor: "#FFB902",
            color: "#042821",
            fontWeight: "semibold",
        },
        row: {
            flexDirection: "row",
            borderTop: "1 solid #042821",
            alignItems: "flex-start",
            justifyContent: "space-between",
            fontSize: 10,
            color: "#3B4C58",
            fontWeight: "medium",
            breakInside: "avoid",
            pageBreakInside: "avoid",
        },
        cellWdith: {
            width: "20%",
            maxWidth: "23%",
            color: "#042821",
            borderRight: "1px solid #042821",
            fontWeight: "normal",
            padding: 8,
        },
        cellWdith3: {
            width: "12%",
            color: "#042821",
            borderRight: "1px solid #042821",
            fontWeight: "normal",
            padding: 8,
        },

        cellWdith6: {
            width: "14%",
            color: "#042821",
            borderRight: "1px solid #042821",
            padding: 8,
            fontWeight: "normal",
        },
        cellWdith5: {
            width: "10%",
            color: "#042821",
            padding: 8,
            fontWeight: "normal",
        },
        profileImg: {
            width: 16,
        },
        innerPadding: {
            padding: 8,
        },
    });

    const subTotalAmount = Number(totalAmount) || 0;
    const taxTotalAmount = Number(taxAmount) || 0;
    const shippingAndHandlingNumber = Number(customerDetails?.shippingAndHandling) || 0;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [displayImage, setDisplayImage] = useState(null);

    useEffect(() => {
        const processAndSetImage = async () => {
            if (imageDetails) {
                try {
                    setLoading(true);
                    setError(null);

                    const base64 = await imageUrlToBase64(imageDetails);
                    if (typeof base64 !== "string" || !base64) {
                        setError("Could not retrieve valid image data from URL.");
                        setDisplayImage(null); // Ensure displayImage is null if data is invalid
                        return; // Exit if base64 is not a valid string
                    }

                    // Step 2: Check if it's an SVG and convert to PNG if necessary
                    // TypeScript now knows 'base64' is a string inside this block
                    if (base64.startsWith("data:image/svg+xml;base64,")) {
                        const svgWidth = 2947.51; // From your SVG's viewBox
                        const svgHeight = 784.34; // From your SVG's viewBox
                        const targetRasterWidth = 600;
                        const targetRasterHeight = (targetRasterWidth / svgWidth) * svgHeight;

                        const pngBase64 = await svgBase64ToPngBase64(
                            base64,
                            targetRasterWidth,
                            targetRasterHeight
                        );
                        setDisplayImage(pngBase64);
                    } else {
                        // It's already a raster image (JPEG, PNG, GIF, WebP), use it directly
                        setDisplayImage(base64);
                    }
                } catch (err) {
                    // It's good practice to ensure error is a string if you expect to display it as such
                    setError(
                        `Failed to load or convert image: ${
                            err instanceof Error ? err.message : String(err)
                        }`
                    );
                    console.error("Image processing error:", err);
                    setDisplayImage(null); // Ensure displayImage is null on error
                } finally {
                    setLoading(false);
                }
            } else {
                // No image URL provided
                setDisplayImage(null);
                setLoading(false);
                setError("No image URL provided."); // Set an error if no URL is given
            }
        };

        processAndSetImage();
    }, [imageDetails]);

    // Conditional rendering based on loading/error states
    if (loading) {
        return (
            <Document>
                <Page size="A4" style={styles.page}>
                    <Text>Loading image for PDF...</Text>
                </Page>
            </Document>
        );
    }

    if (error) {
        return (
            <Document>
                <Page size="A4" style={styles.page}>
                    <Text style={{ color: "red" }}>Error: {error}</Text>
                </Page>
            </Document>
        );
    }
    if (loading) {
        return <Text>Loading image...</Text>;
    }

    if (error) {
        return <Text>Error: {error}</Text>;
    }
    return (
        <Document>
            <Page size="A4" orientation="portrait" style={[styles.page, signFix.page]}>
                <View style={styles.bgWhite}>
                    <View style={[styles.wFull]}>
                        <View style={[headerStyles.container]}>
                            <View style={headerStyles.imageContainer}>
                                {/* {imageDetails && <Image src={imageDetails} style={styles.logo} />} */}
                                {displayImage ? (
                                    <Image src={displayImage} style={styles.logo} />
                                ) : (
                                    <Text>No image to display in PDF.</Text>
                                )}
                            </View>
                            <View>
                                <Text
                                    style={{
                                        fontSize: 16,
                                        fontWeight: "bold",
                                        color: "#042821",
                                        marginBottom: 5,
                                        minWidth: "250px",
                                    }}
                                >
                                    Purchase Order - {`${poPrefix || "PO"}${taId || ""}`}
                                </Text>
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "250px",
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            fontWeight: "medium",
                                            color: "#042821",
                                        }}
                                    >
                                        Issued Date
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            fontWeight: "normal",
                                            color: "#64748B",
                                        }}
                                    >
                                        {customerDetails?.issuedDate
                                            ? moment(customerDetails?.issuedDate).format(
                                                  "MMM DD, YYYY"
                                              )
                                            : "N/A"}
                                    </Text>
                                </View>
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "250px",
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            fontWeight: "medium",
                                            color: "#042821",
                                        }}
                                    >
                                        Due Date
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            fontWeight: "normal",
                                            color: "#64748B",
                                        }}
                                    >
                                        {customerDetails?.issuedDate
                                            ? moment(customerDetails?.dueDate).format(
                                                  "MMM DD, YYYY"
                                              )
                                            : "N/A"}
                                    </Text>
                                </View>
                            </View>
                        </View>

                        <View
                            style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                width: "100%",
                                border: "1px solid #042821",
                                padding: 12,
                                color: "#042821",
                            }}
                        >
                            <View
                                style={{ flexDirection: "row", width: "45%", fontFamily: "Cairo" }}
                            >
                                <Text
                                    style={{
                                        fontSize: 12,
                                        fontWeight: "medium",
                                        marginBottom: 8,
                                        color: "#042821",
                                        paddingRight: 6,
                                    }}
                                >
                                    From:
                                </Text>
                                <View
                                    style={{
                                        fontSize: 12,
                                        fontWeight: "medium",
                                        color: "#042821",
                                    }}
                                >
                                    <Text
                                        style={{
                                            marginBottom: "1px",
                                            flexWrap: "wrap",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {ldbName}
                                    </Text>
                                    <Text
                                        style={{
                                            marginBottom: "1px",
                                            flexWrap: "wrap",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {ldbEmail}
                                    </Text>
                                    <Text
                                        style={{
                                            marginBottom: "1px",
                                            flexWrap: "wrap",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {ldbAddress}
                                    </Text>
                                    <Text
                                        style={{
                                            marginBottom: "1px",
                                            flexWrap: "wrap",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {ldbPhone}
                                    </Text>
                                </View>
                            </View>
                            <View
                                style={{
                                    flexDirection: "row",
                                    maxWidth: "45%",
                                    fontFamily: "Cairo",
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 12,
                                        fontWeight: "medium",
                                        marginBottom: 8,
                                        color: "#042821",
                                        paddingRight: 6,
                                    }}
                                >
                                    To:
                                </Text>
                                <View
                                    style={{
                                        fontSize: 12,
                                        fontWeight: "medium",
                                        color: "#042821",
                                        flexWrap: "wrap",
                                        paddingRight: 12,
                                    }}
                                >
                                    <Text
                                        style={{
                                            marginBottom: "1px",
                                            flexWrap: "wrap",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {vendorName}
                                    </Text>

                                    <Text
                                        style={{
                                            marginBottom: "1px",
                                            flexWrap: "wrap",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {vendorEmail}
                                    </Text>
                                    <Text
                                        style={{
                                            marginBottom: "1px",
                                            flexWrap: "wrap",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {vendorAddress}
                                    </Text>
                                    <Text
                                        style={{
                                            marginBottom: "1px",
                                            flexWrap: "wrap",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {vendorPhone}
                                    </Text>
                                </View>
                            </View>
                        </View>

                        <View style={tableStyles.section}>
                            <View style={tableStyles.container}>
                                <View style={[tableStyles.header, { alignItems: "stretch" }]}>
                                    <Text style={[tableStyles.cellWdith, { fontWeight: "bold" }]}>
                                        Product
                                    </Text>
                                    {/* {hasDescription && ( */}
                                    <Text style={[tableStyles.cellWdith, { fontWeight: "bold" }]}>
                                        Description
                                    </Text>
                                    {/* )} */}
                                    <Text
                                        style={[
                                            tableStyles.cellWdith3,
                                            {
                                                fontWeight: "bold",
                                                display: "flex",
                                                alignItems: "flex-start",
                                            },
                                        ]}
                                    >
                                        Qty
                                    </Text>
                                    <Text style={[tableStyles.cellWdith3, { fontWeight: "bold" }]}>
                                        Unit cost
                                    </Text>
                                    <Text style={[tableStyles.cellWdith3, { fontWeight: "bold" }]}>
                                        Sub total
                                    </Text>
                                    <Text style={[tableStyles.cellWdith6, { fontWeight: "bold" }]}>
                                        Delivery Date
                                    </Text>
                                    <Text
                                        style={[
                                            tableStyles.cellWdith5,
                                            {
                                                fontWeight: "bold",
                                                paddingTop: 8,
                                                paddingBottom: 8,
                                                paddingLeft: 2,
                                                paddingRight: 2,
                                            },
                                        ]}
                                    >
                                        Lead time
                                    </Text>
                                </View>
                                <View style={[tableStyles.tableBody]}>
                                    {containers?.map((item, idx) => (
                                        <View
                                            style={[
                                                tableStyles.row,
                                                {
                                                    alignItems: "stretch",
                                                    display: "flex",
                                                    flexDirection: "row",
                                                },
                                            ]}
                                            key={idx}
                                        >
                                            <View style={tableStyles.cellWdith}>
                                                <View>
                                                    <Text style={{ flexWrap: "wrap" }}>
                                                        {item.productName}
                                                    </Text>
                                                    <Text style={{ flexWrap: "wrap" }}>
                                                        {item.sku}
                                                    </Text>
                                                </View>
                                            </View>
                                            {/* {hasDescription && ( */}
                                            <View style={[tableStyles.cellWdith]}>
                                                <Text style={{ flexWrap: "wrap" }}>
                                                    {item?.description}
                                                </Text>
                                            </View>
                                            {/* )} */}
                                            <Text
                                                style={[
                                                    tableStyles.cellWdith3,
                                                    {
                                                        flexWrap: "wrap",
                                                    },
                                                ]}
                                            >
                                                {formatMoney().format(item?.quantity)}
                                            </Text>
                                            <Text
                                                style={[
                                                    tableStyles.cellWdith3,
                                                    { flexWrap: "wrap" },
                                                ]}
                                            >
                                                {getCurrencyFromCurrencyCode(
                                                    item?.unitCostCurrency ||
                                                        item?.unitCost?.currency
                                                )}
                                                {formatMoney().format(
                                                    item?.unitCostAmount || item?.unitCost?.amount
                                                )}
                                            </Text>
                                            <Text
                                                style={[
                                                    tableStyles.cellWdith3,
                                                    { flexWrap: "wrap" },
                                                ]}
                                            >
                                                {getCurrencyFromCurrencyCode(
                                                    item?.unitCostCurrency ||
                                                        item?.unitCost?.currency
                                                )}
                                                {formatMoney().format(item?.subtotal?.amount)}
                                            </Text>
                                            <Text
                                                style={[
                                                    tableStyles.cellWdith6,
                                                    { flexWrap: "wrap" },
                                                ]}
                                            >
                                                {moment(item.deliveryDate).format("MMM DD, YYYY")}
                                            </Text>
                                            <Text
                                                style={[
                                                    tableStyles.cellWdith5,
                                                    { flexWrap: "wrap" },
                                                ]}
                                            >
                                                {item.leadTime}
                                            </Text>
                                        </View>
                                    ))}
                                </View>
                            </View>
                        </View>
                        <View
                            style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                borderBottom: "1px solid #042821",
                                borderLeft: "1px solid #042821",
                                borderRight: "1px solid #042821",
                                alignItems: "stretch",
                            }}
                        >
                            <View
                                style={{
                                    width: "52%",
                                    borderRight: "1px solid #042821",
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <View>
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: "#042821",
                                            fontWeight: "medium",
                                            padding: 8,
                                        }}
                                    >
                                        Total Quantity: {formatMoney().format(totalQuantity)}
                                    </Text>
                                </View>
                            </View>
                            <View style={{ width: "48%" }}>
                                <View
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "stretch",
                                        borderBottom: "1px solid #042821",
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: "#142837",
                                            fontWeight: "medium",
                                            padding: 6,

                                            borderRight: "1px solid #042821",
                                            width: "50%",
                                        }}
                                    >
                                        Total
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: "#142837",
                                            fontWeight: "medium",
                                            textAlign: "right",
                                            padding: 6,
                                        }}
                                    >
                                        {getCurrencyFromCurrencyCode(customerDetails?.currency)}
                                        {formatMoney().format(totalAmount)}
                                    </Text>
                                </View>

                                <View
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "stretch",
                                        borderBottom: "1px solid #042821",
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: "#142837",
                                            fontWeight: "medium",
                                            padding: 6,
                                            width: "50%",
                                            borderRight: "1px solid #042821",
                                        }}
                                    >
                                        Tax ({customerDetails?.tax}%)
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: "#142837",
                                            fontWeight: "medium",
                                            textAlign: "right",
                                            padding: 6,
                                        }}
                                    >
                                        {getCurrencyFromCurrencyCode(customerDetails?.currency)}
                                        {formatMoney().format(taxAmount || 0)}
                                    </Text>
                                </View>

                                <View
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "stretch",
                                        borderBottom: "1px solid #042821",
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: "#142837",
                                            fontWeight: "medium",
                                            width: "50%",
                                            borderRight: "1px solid #042821",
                                            padding: 6,
                                            paddingRight: 2,
                                        }}
                                    >
                                        Shipping and Handling
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: "#142837",
                                            fontWeight: "medium",
                                            textAlign: "right",
                                            padding: 6,
                                        }}
                                    >
                                        {getCurrencyFromCurrencyCode(customerDetails?.currency)}
                                        {formatMoney().format(
                                            Number(customerDetails?.shippingAndHandling) || 0
                                        )}
                                    </Text>
                                </View>

                                <View
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "stretch",
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            fontWeight: "medium",
                                            padding: 6,
                                            width: "50%",
                                            borderRight: "1px solid #042821",
                                        }}
                                    >
                                        Grand Total Amount
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            fontWeight: "medium",
                                            textAlign: "right",
                                            padding: 6,
                                        }}
                                    >
                                        {getCurrencyFromCurrencyCode(customerDetails?.currency)}
                                        {formatMoney().format(
                                            subTotalAmount +
                                                taxTotalAmount +
                                                shippingAndHandlingNumber
                                        )}
                                    </Text>
                                </View>
                            </View>
                        </View>

                        <View>
                            {filesAttached?.length > 0 && (
                                <View style={{ marginTop: 20 }}>
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: "#142837",
                                            fontWeight: "medium",
                                            marginBottom: 4,
                                        }}
                                    >
                                        File Attachment
                                    </Text>
                                    {filesAttached?.map((doc: any, idx) => (
                                        <View
                                            key={idx}
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                padding: 4,
                                                borderRadius: 4,
                                                borderWidth: 1,
                                                borderColor: "#94A3B8",
                                            }}
                                        >
                                            {/* <Image
                                                src={displayFileIcon(
                                                    getFileTypeFromUrl(doc?.fileName),
                                                    true
                                                )}
                                                style={tableStyles?.profileImg}
                                            /> */}
                                            <View style={{ marginLeft: 8 }}>
                                                <Text
                                                    style={{
                                                        fontSize: 12,
                                                        color: "#042821",
                                                        fontWeight: "medium",
                                                    }}
                                                >
                                                    {doc?.fileName || doc?.filename}
                                                </Text>
                                            </View>
                                        </View>
                                    ))}
                                </View>
                            )}

                            <View
                                style={{
                                    marginTop: 20,
                                    borderBottomWidth: 0.5,
                                    borderBottomColor: "#444444",
                                    paddingBottom: 20,
                                    borderStyle: "dashed",
                                }}
                            >
                                <Text
                                    style={[{ fontWeight: "bold", fontSize: 12, color: "#042821" }]}
                                >
                                    Incoterm
                                </Text>
                                <Text
                                    style={[
                                        { fontWeight: "normal", fontSize: 10, color: "#042821" },
                                    ]}
                                >
                                    {customerDetails?.incoterm ? customerDetails?.incoterm : "N/A"}
                                </Text>
                            </View>
                            <View
                                style={{
                                    marginTop: 20,
                                    borderBottomWidth: 0.5,
                                    borderBottomColor: "#444444",
                                    paddingBottom: 20,
                                    borderStyle: "dashed",
                                }}
                            >
                                <Text
                                    style={[{ fontWeight: "bold", fontSize: 12, color: "#042821" }]}
                                >
                                    Payment terms
                                </Text>
                                <Text
                                    style={[
                                        {
                                            fontWeight: "normal",
                                            fontSize: 10,
                                            color: "#042821",
                                        },
                                    ]}
                                >
                                    {customerDetails?.paymentTerms
                                        ? customerDetails?.paymentTerms?.charAt(0)?.toUpperCase() +
                                          customerDetails?.paymentTerms?.slice(1)
                                        : "N/A"}
                                </Text>
                            </View>
                        </View>

                        {customerDetails?.termsAndCondition && (
                            <View
                                style={{
                                    marginTop: 20,
                                    borderBottomWidth: 0.5,
                                    borderBottomColor: "#444444",
                                    paddingBottom: 20,
                                    borderStyle: "dashed",
                                }}
                            >
                                <Text
                                    style={[
                                        styles.flex,
                                        { fontWeight: "bold", fontSize: 12, color: "#042821" },
                                    ]}
                                >
                                    Terms and Conditions
                                </Text>
                                <View
                                    style={[
                                        { fontWeight: "normal", fontSize: 10, color: "#042821" },
                                    ]}
                                >
                                    <Text>
                                        {hasHTMLTag(customerDetails?.termsAndCondition)
                                            ? sanitizeHtml(customerDetails?.termsAndCondition, true)
                                                  .sanitizedHTML
                                            : customerDetails?.termsAndCondition}
                                        hello here
                                    </Text>
                                </View>
                            </View>
                        )}

                        {customerDetails?.additionalInfo && (
                            <View
                                style={{
                                    marginTop: 20,
                                    borderBottomWidth: 0.5,
                                    borderBottomColor: "#444444",
                                    paddingBottom: 20,
                                    borderStyle: "dashed",
                                }}
                            >
                                <Text
                                    style={[
                                        styles.flex,
                                        { fontWeight: "bold", fontSize: 12, color: "#042821" },
                                    ]}
                                >
                                    Additional Information
                                </Text>
                                <View
                                    style={[
                                        { fontWeight: "normal", fontSize: 10, color: "#042821" },
                                    ]}
                                >
                                    <Text style={{ width: "80%" }}>
                                        {customerDetails?.additionalInfo}
                                    </Text>
                                </View>
                            </View>
                        )}
                    </View>
                </View>

                <Text
                    fixed
                    style={styles.pageNumber}
                    render={({ pageNumber, totalPages }) =>
                        pageNumber === totalPages && (
                            <Text
                                style={{
                                    fontSize: 10,
                                    color: "#042821",
                                    fontWeight: "medium",
                                }}
                            >
                                Powered by{` `}
                                <Text style={{ color: "#FFB902", fontWeight: "bold" }}>Synth</Text>
                            </Text>
                        )
                    }
                />
            </Page>
        </Document>
    );
};

export default PurchaseOrdersPDF;
